import { useEffect, useState } from 'react';
import {
    TermForm,
    VendorContractRow,
} from '../../../../../slices/marketplace/loi-form/term-form';
import {
    DataTable,
    DataTableView,
} from '../../../../../models/DataRequestHub/DataTable';
import { Timeline } from '../../../../../slices/marketplace/timeline/models/timeline.model';
import { TimelineActionType } from '../../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import {
    AnswerTypeVariable,
    AnswerTypeVariableNamesList,
} from '../../../../../models/DataRequestHub/ProjectVariable';
import { formatDataTableCellDisplayValue } from '../../../../../utils/dataTableCellDisplayValue';

export interface VendorContractsInitResult {
    hasVendorContracts: boolean;
    vendorContractColumns: string[];
    vendorContractRowDisplayData: Record<number, Record<string, string>>;
    vendorContractTableRows: Array<{ tableRowId: number }>;
    vendorContractProjectId: number | null;
    vendorContractTableId: number | null;
    vendorContractFileColumnName: string | null;
    vendorContractFileColumnId: number | null;
}

interface UseVendorContractsInitProps {
    engagementId: number;
    termForm: TermForm | undefined | null;
    onChange: (termForm: TermForm) => void;
    axios: any;
}

function findTableAndViewByViewId(
    tables: DataTable[],
    viewId: number
): { dataTable: DataTable; dataView: DataTableView } | null {
    for (const t of tables) {
        const v = (t.views ?? []).find(
            (v) => v.id === viewId || v.originalTableViewId === viewId
        );
        if (v) return { dataTable: t, dataView: v };
    }
    return null;
}

export function useVendorContractsInit({
    engagementId,
    termForm,
    onChange,
    axios,
}: UseVendorContractsInitProps): VendorContractsInitResult {
    const [hasVendorContracts, setHasVendorContracts] =
        useState<boolean>(false);
    const [vendorContractColumns, setVendorContractColumns] = useState<
        string[]
    >([]);
    const [vendorContractRowDisplayData, setVendorContractRowDisplayData] =
        useState<Record<number, Record<string, string>>>({});
    const [vendorContractTableRows, setVendorContractTableRows] = useState<
        Array<{ tableRowId: number }>
    >([]);
    const [visibleRowIds, setVisibleRowIds] = useState<Set<number> | null>(
        null
    );
    const [vendorContractProjectId, setVendorContractProjectId] = useState<
        number | null
    >(null);
    const [vendorContractTableId, setVendorContractTableId] = useState<
        number | null
    >(null);
    const [vendorContractFileColumnName, setVendorContractFileColumnName] =
        useState<string | null>(null);
    const [
        vendorContractFileColumnId,
        setVendorContractFileColumnId,
    ] = useState<number | null>(null);

    useEffect(() => {
        if (!visibleRowIds || !termForm) return;
        const currentRows = termForm.vendorContractRows;
        if (!currentRows || currentRows.length === 0) return;
        const syncedRows = currentRows.filter((r) =>
            visibleRowIds.has(r.tableRowId)
        );
        if (syncedRows.length !== currentRows.length) {
            onChange({ ...termForm, vendorContractRows: syncedRows });
        }
    }, [visibleRowIds, termForm, onChange]);

    useEffect(() => {
        if (!axios || !engagementId) return;

        let cancelled = false;

        (async () => {
            try {
                const engagementResponse = await axios.get(
                    `/api/Marketplace/Engagements/${engagementId}`
                );
                if (cancelled || engagementResponse.status !== 200) return;

                const timeline: Timeline = engagementResponse.data;
                const engagementData = timeline.engagementData;

                const brvcEvent = timeline.events?.find(
                    (e) =>
                        e.linkedAction?.actionType ===
                        TimelineActionType.BuyerReviewVendorContracts
                );
                const brvcDoc = brvcEvent?.linkedAction?.referenceItemId
                    ? engagementData.documentsList?.find(
                          (d) =>
                              d.id === brvcEvent.linkedAction!.referenceItemId
                      )
                    : null;

                if (!brvcDoc?.linkedProjectId || !brvcDoc?.dataTableViewId) {
                    setHasVendorContracts(false);
                    return;
                }

                const linkedProject = engagementData.dataRequestProjects.find(
                    (p) => p.id === brvcDoc.linkedProjectId
                );

                if (!linkedProject?.dataRequestProjectId) {
                    setHasVendorContracts(false);
                    return;
                }

                setHasVendorContracts(true);

                try {
                    const projectResponse = await axios.get(
                        `/api/DataRequestProjects/${linkedProject.dataRequestProjectId}`
                    );
                    if (cancelled || projectResponse.status !== 200) return;

                    const tables: DataTable[] =
                        projectResponse.data.dataTables ?? [];

                    const found = findTableAndViewByViewId(
                        tables,
                        brvcDoc.dataTableViewId
                    );
                    if (!found) return;
                    const { dataTable, dataView } = found;

                    const includedColumnIds = new Set(
                        (dataView.viewColumns ?? [])
                            .filter((vc) => vc.isIncluded)
                            .map((vc) => vc.customColumnId)
                    );
                    const includedColumns = dataTable.columns.filter((c) =>
                        includedColumnIds.has(c.customColumnId)
                    );
                    const columnNames = includedColumns.map((c) => c.name);
                    setVendorContractColumns(columnNames);

                    const fileTypeName = AnswerTypeVariableNamesList.find(
                        (f) => f.id === AnswerTypeVariable.File
                    )?.name;
                    const fileCol =
                        includedColumns.find(
                            (c) => c.answerType === fileTypeName
                        ) ?? null;
                    setVendorContractProjectId(
                        linkedProject.dataRequestProjectId
                    );
                    setVendorContractTableId(dataTable.id);
                    setVendorContractFileColumnName(fileCol?.name ?? null);
                    setVendorContractFileColumnId(
                        fileCol?.id ?? null
                    );

                    const columnIdToColumn = new Map(
                        includedColumns.map((c) => [c.customColumnId, c])
                    );
                    const visibleRows = (dataTable.rows ?? []).filter(
                        (r) => r.isVisible
                    );

                    const rowDisplayData: Record<number, Record<string, string>> = {};
                    for (const row of visibleRows) {
                        const cells: Record<string, string> = {};
                        for (const cell of row.cells ?? []) {
                            const column = columnIdToColumn.get(cell.customColumnId);
                            if (column) {
                                cells[column.name] = formatDataTableCellDisplayValue(
                                    column,
                                    cell.answerText ?? ''
                                );
                            }
                        }
                        rowDisplayData[row.id] = cells;
                    }
                    setVendorContractRowDisplayData(rowDisplayData);
                    setVendorContractTableRows(
                        visibleRows.map((r) => ({ tableRowId: r.id }))
                    );
                    setVisibleRowIds(new Set(visibleRows.map((r) => r.id)));
                } catch (err) {
                    console.error(
                        '[VendorContracts] Failed to fetch DR Hub data:',
                        err
                    );
                }
            } catch (err) {
                console.error(
                    '[VendorContracts] Failed to load engagement data:',
                    err
                );
                setHasVendorContracts(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [axios, engagementId]);

    return {
        hasVendorContracts,
        vendorContractColumns,
        vendorContractRowDisplayData,
        vendorContractTableRows,
        vendorContractProjectId,
        vendorContractTableId,
        vendorContractFileColumnName,
        vendorContractFileColumnId,
    };
}
